import { CHANGE_LABEL } from './CompareReport';

const CHANGE_COLOR = {
  'new-failure': [176, 42, 42],
  fixed: [14, 107, 67],
  'still-failing': [75, 85, 99],
  'status-changed': [28, 92, 171],
  'perf-regression': [183, 121, 31],
  'perf-improvement': [14, 107, 67],
};

const meta = (r) =>
  [r?.branch, r?.commit && `@ ${String(r.commit).slice(0, 8)}`, r?.environment, r?.startTime && new Date(r.startTime).toLocaleDateString()]
    .filter(Boolean)
    .join('  ·  ');

/**
 * Build a proper multi-page A4 PDF (real text + paginated tables, no image
 * slicing) from the comparison data and trigger a download.
 */
export async function exportCompareReportPdf({ project, runA, runB, summary, rows }) {
  const { default: JsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new JsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const M = 48;

  // Masthead
  doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(17, 24, 39);
  doc.text('WP QA TESTING DASHBOARD', M, M);
  doc.setFontSize(20).text('Run comparison report', M, M + 24);
  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(107, 114, 128);
  doc.text(project || 'All projects', pageW - M, M, { align: 'right' });
  doc.text(`Generated ${new Date().toLocaleString()}`, pageW - M, M + 13, { align: 'right' });
  doc.setDrawColor(17, 24, 39).setLineWidth(1.5).line(M, M + 34, pageW - M, M + 34);

  // Run boxes
  const boxY = M + 46;
  const boxW = (pageW - 2 * M - 16) / 2;
  [['RUN A · BASELINE', runA, M], ['RUN B · CANDIDATE', runB, M + boxW + 16]].forEach(([label, r, x]) => {
    doc.setDrawColor(229, 231, 235).setLineWidth(1).roundedRect(x, boxY, boxW, 52, 4, 4);
    doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(156, 163, 175).text(label, x + 12, boxY + 15);
    doc.setFontSize(11).setTextColor(17, 24, 39).text(`${r?.name || 'Run'} #${String(r?.id || '').slice(0, 8)}`, x + 12, boxY + 30);
    doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(107, 114, 128).text(meta(r), x + 12, boxY + 43);
  });

  // Summary table
  doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(17, 24, 39).text('Summary', M, boxY + 82);
  autoTable(doc, {
    startY: boxY + 90,
    margin: { left: M, right: M },
    head: [['Metric', 'Run A', 'Run B', 'Change']],
    body: summary.map((s) => [s.label, String(s.a), String(s.b), s.delta || '']),
    styles: { fontSize: 9, cellPadding: 5 },
    headStyles: { fillColor: [17, 24, 39], textColor: 255, fontSize: 8 },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right', fontStyle: 'bold' } },
  });

  // Changes table
  let y = doc.lastAutoTable.finalY + 24;
  doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(17, 24, 39).text(`Changes (${rows.length})`, M, y);
  autoTable(doc, {
    startY: y + 8,
    margin: { left: M, right: M },
    head: [['Test', 'Suite', 'A → B', 'Δ ms', 'Change']],
    body: rows.map((r) => {
      const d = (r.b?.duration ?? 0) - (r.a?.duration ?? 0);
      return [
        r.title,
        r.suite || '',
        `${r.a?.status || '—'} → ${r.b?.status || '—'}`,
        `${d > 0 ? '+' : ''}${d}`,
        CHANGE_LABEL[r.change] || r.change,
      ];
    }),
    styles: { fontSize: 8.5, cellPadding: 4, overflow: 'linebreak' },
    headStyles: { fillColor: [17, 24, 39], textColor: 255, fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 190 },
      2: { fontSize: 7.5, textColor: [107, 114, 128] },
      3: { halign: 'right', fontStyle: 'bold' },
      4: { fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      const row = rows[data.row.index];
      if (data.column.index === 4 && CHANGE_COLOR[row.change]) {
        data.cell.styles.textColor = CHANGE_COLOR[row.change];
      }
      if (data.column.index === 3) {
        const d = (row.b?.duration ?? 0) - (row.a?.duration ?? 0);
        data.cell.styles.textColor = d > 0 ? [176, 42, 42] : d < 0 ? [14, 107, 67] : [107, 114, 128];
      }
    },
    didDrawPage: () => {
      const p = doc.internal.pageSize;
      doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(156, 163, 175);
      doc.text('WP QA Testing Dashboard · compare report', M, p.getHeight() - 24);
      doc.text(
        `Page ${doc.internal.getNumberOfPages()}`,
        p.getWidth() - M,
        p.getHeight() - 24,
        { align: 'right' },
      );
    },
  });

  doc.save(`compare-report-${String(runB?.id || '').slice(0, 6)}.pdf`);
}
